import {
  useAttachPlayVideoMutation,
  useInitializePlayVideoMutation,
  useUploadPlayVideoMutation,
} from "../redux/slices";
import { SynergyClip } from "../redux/slices/synergy-api/types";
import { getVideoDuration } from "../utils";

export interface UploadVideosProps {
  playId: string;
  setUploadingVideo: (uploading: boolean) => void;
}

export const useUploadVideos = ({ playId, setUploadingVideo }: UploadVideosProps) => {
  const [attachPlayVideo] = useAttachPlayVideoMutation();
  const [initializeVideoUpload] = useInitializePlayVideoMutation();
  const [uploadPlayVideo] = useUploadPlayVideoMutation();

  const uploadSynergyClips = async (clips: SynergyClip[]) => {
    for (const clip of clips) {
      // Handle new version of synergy editor where trim in is relative to
      // the whole asset.  Check if trimIn is greater than startIn.
      let playbackIn = clip.trimIn;
      let playbackOut = clip.trimOut;

      if (clip.trimIn && clip.startIn && clip.trimOut && clip.trimIn > clip.startIn) {
        playbackIn = clip.trimIn - clip.startIn;
        playbackOut = clip.trimOut - clip.startIn;
      }

      let duration;
      if (clip.trimOut && clip.trimIn) {
        duration = Math.max(clip.trimOut - clip.trimIn, 1);
      } else {
        duration = Math.max(clip.startOut - clip.startIn, 1);
      }

      const body = {
        title: `${clip.title} - ${clip.result}`,
        description: clip.comments,
        url: clip.videoUrl,
        previewUrl: clip.thumbnailUrl,
        uploadComplete: true,
        externalSourceId: clip.id,
        source: "synergy",
        playbackIn,
        playbackOut,
        duration,
        length: 1,
        "mime-type": "video/mp4",
        appKey: "FASTDRAW",
      };

      attachPlayVideo({ body, playId });
    }
  };

  const uploadLocalVideos = async (files: FileList) => {
    const selectedFile = files[0];

    if (selectedFile) {
      const duration = await getVideoDuration(selectedFile);
      const uploadResponse = await initializeVideoUpload({
        duration,
        identifier: `${selectedFile.name}-${selectedFile.size}`,
        title: selectedFile.name,
        length: selectedFile.size,
        size: selectedFile.size,
        playbackOut: await getVideoDuration(selectedFile),
        playId: playId,
      }).unwrap();

      const requestData: any = {
        fileName: selectedFile.name,
        length: selectedFile.size,
        duration,
        app: "FastDraw",
      };

      if (uploadResponse[0]) {
        const formData = new FormData();
        requestData["callbackUrl"] = uploadResponse[0].callbackUrl;
        formData.append("file", selectedFile);
        formData.append("uploadRequest", JSON.stringify(requestData));
        setUploadingVideo(true);
        await uploadPlayVideo({ formData, playId });
        setUploadingVideo(false);
      }
    }
  };

  return { uploadSynergyClips, uploadLocalVideos };
};
